import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import { AppProvider } from "../context/AppContext";

// Eagerly loaded components
import Login from "../pages/auth/Login";
import Layout from "../components/Layout";
import { PrivateRoutes, PublicRoutes } from "../privateRoutesCheck";
import AdminDevices from "../pages/ManageAdmins/AdminDevices";
import AdminFleets from "../pages/ManageAdmins/AdminFleets";
import FleetDevices from "../pages/ManageFleets/FleetDevices";
import UserFleets from "../pages/ManageUsers/UserFleets";
import UserDevices from "../pages/ManageUsers/UserDevices";
import AssignFleets from "../pages/ManageUsers/AssignFleets";
import AssignRights from "../pages/ManageUsers/AssignRights";

const ManageAdmins = lazy(() => import("../pages/ManageAdmins"));
const Profile = lazy(() => import("../pages/Profile"));
const ChangePassword = lazy(() => import("../pages/Profile/ChangePassword"));
const ManageCategories = lazy(() => import("../pages/ManageCategories"));
const ManageFleets = lazy(() => import("../pages/ManageFleets"));
const ManageJsonConfigurations = lazy(
  () => import("../pages/ManageJsonConfigurations"),
);
const Provisioning = lazy(() => import("../pages/Provisioning"));
const ProvisionDevices = lazy(
  () => import("../pages/Provisioning/ProvisionDevices"),
);
const Settings = lazy(() => import("../pages/Profile/Settings"));
const ManageSchedulers = lazy(() => import("../pages/ManageSchedulers"));
const ManageOtaUpdates = lazy(() => import("../pages/ManageOtaUpdates"));
const SelectSchedulerFleets = lazy(
  () => import("../pages/ManageSchedulers/SelectFleet"),
);

const SelectSchedulerDevices = lazy(
  () => import("../pages/ManageSchedulers/SelectDevices"),
);
const AddScheduler = lazy(
  () => import("../pages/ManageSchedulers/AddScheduler"),
);
const Scheduler = lazy(() => import("../pages/ManageSchedulers/Scheduler"));
const Manageuser = lazy(() => import("../pages/ManageUsers"));
const Loggers = lazy(() => import("../pages/ManageLogger/LoggerTable"));
const MqttLogs = lazy(() => import("../pages/ManageMqttLogs/MqttLogs"));
const TransactionsTable = lazy(
  () => import("../pages/ManageTransactions/TransactionsTable"),
);
const Summary = lazy(() => import("../pages/Summary/index"));
const Advertisement = lazy(() => import("../pages/Advertisement"));
const AdvertisementReportPage = lazy(() => import("../pages/Advertisement/AdvertisementReportPage"));
const FleetSelectionPage = lazy(() => import("../pages/Advertisement/FleetSelectionPage"));
const DeviceSelectionPage = lazy(() => import("../pages/Advertisement/DeviceSelectionPage"));
const AdvertisementSchedulePage = lazy(() => import("../pages/Advertisement/AdvertisementSchedulePage"));

const OtaUpdate = lazy(() => import("../pages/ManageOtaUpdates/OtaUpdate"));
const SelectOtaDevices = lazy(
  () => import("../pages/ManageOtaUpdates/SelectDevices"),
);
const SelectOtaFleets = lazy(
  () => import("../pages/ManageOtaUpdates/SelectFleet"),
);
const AddOtaUpdate = lazy(
  () => import("../pages/ManageOtaUpdates/AddOtaUpdate"),
);

const SwaggerDocs = lazy(() => import("../components/common/SwaggerDocs"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const SearchQeury = lazy(
  () => import("../pages/Dashboard/FleetDetails/SearchQuery"),
);
const DeviceAnalytics = lazy(
  () => import("../components/common/DeviceAnalytics/DeviceAnalytics"),
);
const UserDeviceAnalytics = lazy(
  () => import("../components/common/DeviceAnalytics/UserDeviceAnalytics"),
);
const AlertRules = lazy(() => import("../pages/AlertRules"));
const ViewAlerts = lazy(() => import("../pages/ViewAlerts"));
const AlertRuleDevices = lazy(
  () => import("../pages/ViewAlerts/AlertRuleDevices"),
);
const SelectAlertFleets = lazy(
  () => import("../pages/AlertRules/SelectFleets"),
);
const Default = lazy(() => import("../pages/Page404/default"));
const NotFound = lazy(() => import("../pages/Page404/page404"));
const SubmitQuery = lazy(() => import("../pages/AlertRules/SubmitQuery"));
const LayoutWithoutSidebar = lazy(
  () => import("../components/LayoutWithoutSidebar"),
);

const routerAdmin = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<PublicRoutes />}>
        <Route path="/login" element={<Login />} />
      </Route>
      <Route element={<PrivateRoutes />}>
        <Route path="/" element={<Layout />}>
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/api-docs" element={<SwaggerDocs />} />
          <Route path="/change_password" element={<ChangePassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="dashboard/devices-analytics/:imei"
            element={<DeviceAnalytics />}
          />
          <Route
            path="devices-analytics/:imei"
            element={<UserDeviceAnalytics />}
          />
          <Route path="/dashboard/search-query" element={<SearchQeury />} />
          <Route path="/admins" element={<ManageAdmins />} />
          <Route path="/admins/:adminId/fleets" element={<AdminFleets />} />
          <Route
            path="/admins/:adminId/fleets/:fleetId/devices"
            element={<AdminDevices />}
          />
          <Route path="/users" element={<Manageuser />} />
          <Route path="/users/:userId/fleets" element={<UserFleets />} />
          <Route
            path="/users/:userId/fleets/:fleetId/devices"
            element={<UserDevices />}
          />
          <Route path="/categories" element={<ManageCategories />} />
          <Route path="/fleets" element={<ManageFleets />} />
          <Route
            path="/json_configurations"
            element={<ManageJsonConfigurations />}
          />
          <Route path="/provisioning_jobs" element={<Provisioning />} />
          <Route
            path="/provisioning_jobs/:jobId/devices/:action"
            element={<ProvisionDevices />}
          />
          <Route path="/fleets/:fleetId/devices" element={<FleetDevices />} />
          <Route path="/schedulers" element={<ManageSchedulers />} />
          <Route path="/schedulers/:id/:status" element={<Scheduler />} />
          <Route path="/ota_updates" element={<ManageOtaUpdates />} />
          <Route path="/ota_updates/:id/:status" element={<OtaUpdate />} />
          <Route path="/loggers" element={<Loggers />} />
          <Route path="/mqttlogs" element={<MqttLogs />} />
          <Route path="/transactions" element={<TransactionsTable />} />
          <Route path="/summary" element={<Summary />} />
          <Route path="/advertisements" element={<Advertisement />} />
          <Route path="/report"   element={<AdvertisementReportPage />} />
          <Route path="/alert_rules" element={<AlertRules />} />
          <Route path="/view-alerts" element={<ViewAlerts />} />
          <Route
            path="/alert_rule_devices/:id/:name"
            element={<AlertRuleDevices />}
          />
          <Route path="/alert-rule-query" element={<SubmitQuery />} />
        </Route>
        <Route path="/" element={<LayoutWithoutSidebar />}>
          <Route
            path="/users/:userId/assign-fleets"
            element={<AssignFleets />}
          />
          <Route
            path="/users/:userId/assign-rights"
            element={<AssignRights />}
          />
          <Route
            path="/scheduler-select-fleet"
            element={<SelectSchedulerFleets />}
          />
          <Route
            path="/scheduler-select-device"
            element={<SelectSchedulerDevices />}
          />
          <Route path="/add-scheduler" element={<AddScheduler />} />
          <Route path="/ota-select-fleet" element={<SelectOtaFleets />} />
          <Route path="/ota-select-device" element={<SelectOtaDevices />} />
          <Route path="/add-ota-update" element={<AddOtaUpdate />} />
          <Route path="/alert-select-fleets" element={<SelectAlertFleets />} />
          <Route path="/fleet"    element={<FleetSelectionPage />} />
          <Route path="/devices"  element={<DeviceSelectionPage />} />
          <Route path="/schedule" element={<AdvertisementSchedulePage />} />
        </Route>
        <Route path="/401" element={<Default />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </>,
  ),
);

const Router = () => {
  const { loading } = useSelector((state) => state.common);

  return (
    <AppProvider>
      <Spin spinning={loading} fullscreen />
      <Suspense fallback={<Spin fullscreen />}>
        <RouterProvider router={routerAdmin} />
      </Suspense>
    </AppProvider>
  );
};

export default Router;
